import { Injectable } from '@angular/core';
import { AnalyticsEvent } from '../models/analytics-event';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

const EVENTS_QUEUE_INTERVAL_IN_MS = 5 * 1000; // 5 seconds

@Injectable({
  providedIn: 'root',
})
export class EventsQueue {
  /**
   * List of events waiting to be sent to the server.
   */
  events: AnalyticsEvent[];

  /**
   * List of events that are currently on their way to the server,
   * but for which a response has not been received yet.
   */
  processingEvents: AnalyticsEvent[];

  /**
   * Whether the task that checks the events and sends them to the server is running or not.
   * When the events queue is empty and all the events have been sent to the server, the recurring tasks
   * stops processing...
   */
  runningTask = null;

  constructor(private http: HttpClient) {
    this.events = [];
    this.processingEvents = [];
  }

  addEvent(event: AnalyticsEvent) {
    console.debug(`EventsQueue -> addEvent`, event);
    this.events.push(event);
    if (!this.runningTask) {
      console.debug(
        `EventsQueue -> queue processing task was not started. Starting now...`
      );
      this.startRunningTask();
    }
  }

  submitEvents() {
    this.processingEvents = [...this.events];
    this.events = [];
    console.debug(
      `EventsQueue -> Sending ${this.processingEvents.length} events.`
    );
    this.http
      .post<{ id: string }>(
        `${environment.PWA_API_URL}/api/analytics/collect`,
        { events: this.processingEvents },
        {
          params: {
            bs_ce: navigator?.cookieEnabled,
          },
          withCredentials: true, // to send the userId in the signed cookies
        }
      )
      .subscribe(
        (res) => {},
        (err) => {
          console.error(
            `An error has occurred while sending analytics events to API.`,
            err
          );
        },
        () => {
          this.processingEvents = []; // clear the processing events queue whether it was a success or not.
        }
      );
  }

  stopRunningTask() {
    clearInterval(this.runningTask);
  }

  startRunningTask() {
    this.runningTask = setInterval(() => {
      /**
       * Sends events only if there are any in the queue AND if there are none on the way to the server.
       */
      if (this.events.length && !this.processingEvents.length) {
        this.submitEvents();
      } else if (!this.processingEvents && !this.events.length) {
        console.debug(
          `EventsQueue -> queue processing task is running but there are no events. Stopping...`
        );
        this.stopRunningTask();
      }
    }, EVENTS_QUEUE_INTERVAL_IN_MS);
  }

  public clearQueue() {
    if (this.runningTask) {
      console.debug(
        `Page is about to be closed. Analytics Events Queue clearing...`
      );
      this.stopRunningTask();
      this.submitEvents();
    }
  }
}
